import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { TextInput, Error } from '../LoginPage/LoginPage.styles';
import { useDispatch } from 'react-redux';
import { changeLoginStatus } from '../../actions/index';
import environment from '../../environment';
import Tooltip from 'rc-tooltip';
import {
  Container,
  Holder,
  HaveAccountContainer,
  FormContainer,
  Title,
  ServerError,
  RegistrationButton,
  ServerErrorMessage,
  InfoIcon,
  FormItem,
} from './SignUpExpert.styles';
import { configure, handleError } from 'services/ErrorService/ErrorService';
import * as SessionAnalyticsService from 'services/SessionAnalyticsService/SessionAnalyticsService';
import { ResetLink } from '../LoginPage/LoginPage.styles';
import { PhoneInputField } from './PhoneInputField/PhoneInputField';
import { isValidPhoneNumber } from 'react-phone-number-input';
import makeRequest from '../../api/makeRequest';
import AuthService from 'services/authService';
import * as SimplrErrors from '@asurion-core/typescript-utils/SimplrErrors';
const SimplrPassword = require('@asurion-core/typescript-utils/SimplrPassword');

const simplrPasswordValidator = new SimplrPassword.SimplrADPasswordValidator();

const signUpSchema = z
  .object({
    firstName: z.string().min(1, 'Please enter a first name'),
    lastName: z.string().min(1, 'Please enter a last name'),
    email: z.email('Please enter a valid email address'),
    phoneNumber: z
      .string()
      .min(1, 'Please enter a phone number')
      .refine(
        (value) => isValidPhoneNumber(value),
        'Please enter a valid phone number with country code (up to 15 digits in the correct format)'
      ),
    password: z.string().min(1, 'Password is required'),
    passwordVerify: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.passwordVerify, {
    message: 'Passwords should match',
    path: ['passwordVerify'],
  })
  .superRefine((data, ctx) => {
    const passwordValidation = simplrPasswordValidator.validate(data.password, data.email);
    if (passwordValidation.status !== SimplrPassword.types.PasswordValidationStatus.VALID) {
      ctx.addIssue({
        message: passwordValidation.errors?.[0]?.message || 'Password is invalid',
        path: ['password'],
      });
    }
  });

export const SignUpGeneric = () => {
  const dispatch = useDispatch();
  const [serverError, setServerError] = useState('');
  const token = new URLSearchParams(window.location.search).get('token') || '';

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, touchedFields },
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(signUpSchema),
    mode: 'onTouched',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      password: '',
      passwordVerify: '',
    },
  });

  const phoneNumber = watch('phoneNumber');

  useEffect(() => {
    AuthService.current() && AuthService.logout();
  }, []);

  const onSubmit = async (data) => {
    const { passwordVerify, ...userData } = data;
    userData.token = token;

    try {
      const URL = `${environment.services.authServiceUrl}/v1/createAccount`;
      const { result: response } = await makeRequest(URL, {
        method: 'POST',
        data: userData,
      });

      await AuthService.login(response.sessionToken);
      dispatch(changeLoginStatus(true));

      /** NOTE(apoorvyadav1|SPD-13362|2025-10-15):
       * Previously, FullStory was initialized when the website loaded, which is not ideal for privacy compliance.
       * Initialize FullStory if user is logged in and it's not already initialized
       */
      SessionAnalyticsService.initializeSessionAnalyticsServices();

      SessionAnalyticsService.identify(response.userId, {
        name: data.firstName + ' ' + data.lastName,
        email: data.email,
        inviteToken: token,
      });

      SessionAnalyticsService.event('Registered', { inviteToken: token });

      configure((scope) => {
        scope.setUser({
          name: data.firstName + ' ' + data.lastName,
          id: response.simplrExpertObjId,
          email: data.email,
          username: data.email,
        });
        // don't create specialists via this flow
        scope.setExtra('isAdmin', false);
      });

      if (window.Intercom) {
        window.Intercom('update', {
          app_id: environment.intercom.intercomId,
          name: data.firstName + ' ' + data.lastName,
          email: data.email,
          user_id: response.simplrExpertObjId,
        });
      }

      // force a refresh when changing the URL so the user loader runs again
      window.location.href = '/logic-builder/';
    } catch (error) {
      handleError({ error });

      if (error.response?.code === SimplrErrors.Codes.WEAK_PASSWORD) {
        setServerError(error.response.fieldErrors[0]);
      } else if (typeof error?.message === 'string') {
        setServerError(error.message);
      } else {
        setServerError('Login failed');
      }
    }
  };

  if (!token) {
    return (
      <Container>
        <Holder>
          <FormContainer>
            <Title>Access Restricted</Title>
            <ServerError>
              This sign up page is invitation only and requires a valid token. Please check your invitation link.
            </ServerError>
          </FormContainer>
        </Holder>
      </Container>
    );
  }

  return (
    <Container>
      <Holder>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormContainer>
            {/* TODO add maybe Asurion logo? */}
            <Title>Let's get started</Title>
            <FormItem>
              <TextInput
                {...register('firstName')}
                type="text"
                placeholder="First Name"
                className={errors.firstName && touchedFields.firstName ? 'error' : ''}
              />
              {errors.firstName && touchedFields.firstName && <Error>{errors.firstName.message}</Error>}
            </FormItem>
            <FormItem>
              <TextInput
                {...register('lastName')}
                type="text"
                placeholder="Last Name"
                className={errors.lastName && touchedFields.lastName ? 'error' : ''}
              />
              {errors.lastName && touchedFields.lastName && <Error>{errors.lastName.message}</Error>}
            </FormItem>
            <FormItem>
              <TextInput
                {...register('email')}
                type="text"
                placeholder="Email"
                className={errors.email && touchedFields.email ? 'error' : ''}
              />
              {errors.email && touchedFields.email && <Error>{errors.email.message}</Error>}
            </FormItem>
            <FormItem>
              <PhoneInputField
                value={phoneNumber}
                onChange={(value) =>
                  setValue('phoneNumber', value, { shouldTouch: false, shouldValidate: touchedFields.phoneNumber })
                }
                onBlur={() => setValue('phoneNumber', phoneNumber, { shouldTouch: true, shouldValidate: true })}
                error={errors.phoneNumber && touchedFields.phoneNumber ? errors.phoneNumber.message : ''}
              />
              <Tooltip
                placement="bottom"
                overlay={'Please enter your phone number in the format +12345678901'}
                arrowContent={<div className="rc-tooltip-arrow-inner" />}
              >
                <InfoIcon
                  src="/images/info_icon.svg"
                  alt="info-icon"
                  phoneNumberError={errors.phoneNumber && touchedFields.phoneNumber ? errors.phoneNumber.message : ''}
                />
              </Tooltip>
            </FormItem>
            <FormItem>
              <TextInput
                {...register('password')}
                type="password"
                autoComplete="new-password"
                placeholder="Password"
                className={errors.password && touchedFields.password ? 'error' : ''}
              />
              {errors.password && touchedFields.password && <Error>{errors.password.message}</Error>}
              <Tooltip
                placement="bottom"
                overlay={'10 characters | 1 uppercase | 1 lowercase | 1 digit'}
                arrowContent={<div className="rc-tooltip-arrow-inner" />}
              >
                <InfoIcon
                  src="/images/info_icon.svg"
                  alt="info-icon"
                  passwordError={errors.password && touchedFields.password ? errors.password.message : ''}
                  passwordVerifyError={
                    errors.passwordVerify && touchedFields.passwordVerify ? errors.passwordVerify.message : ''
                  }
                />
              </Tooltip>
            </FormItem>
            <FormItem>
              <TextInput
                {...register('passwordVerify')}
                type="password"
                autoComplete="new-password"
                placeholder="Re-type password"
                className={errors.passwordVerify && touchedFields.passwordVerify ? 'error' : ''}
              />
              {errors.passwordVerify && touchedFields.passwordVerify && <Error>{errors.passwordVerify.message}</Error>}
            </FormItem>
            <HaveAccountContainer>
              Already have an account?&nbsp;&nbsp;
              <ResetLink to="/login">Log in now!</ResetLink>
            </HaveAccountContainer>
          </FormContainer>
          <ServerErrorMessage hide={!serverError}>
            <ServerError>{serverError}</ServerError>
          </ServerErrorMessage>
          <RegistrationButton type="submit" disabled={!isValid}>
            Sign up
          </RegistrationButton>
        </form>
      </Holder>
    </Container>
  );
};
